import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Search, MessageCircle, Mail } from 'lucide-react';

export function FAQPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Questions', count: 20 },
    { id: 'account', name: 'Account & Setup', count: 5 },
    { id: 'funding', name: 'Funding & Deposits', count: 6 },
    { id: 'security', name: 'Security & Compliance', count: 4 },
    { id: 'technical', name: 'Technical Support', count: 5 },
  ];

  const faqs = [
    {
      id: 1,
      category: 'account',
      question: 'How do I create a TapTapUp account?',
      answer: 'Creating an account is simple. Visit our registration page, provide your email address, create a secure password, and complete the identity verification process. You\'ll need a government-issued ID and proof of address to get started.'
    },
    {
      id: 2,
      category: 'funding',
      question: 'What payment methods do you support?',
      answer: 'We support multiple payment methods including Cash App, Apple Pay, Google Pay, and credit/debit cards. All payment methods are processed through secure, compliant channels with industry-standard encryption.'
    },
    {
      id: 3,
      category: 'funding',
      question: 'What are the minimum and maximum deposit limits?',
      answer: 'The minimum deposit is $10, and the maximum is $10,000 per transaction. Daily limits may vary based on your account verification level and payment method. Premium verified accounts have higher limits.'
    },
    {
      id: 4,
      category: 'security',
      question: 'Is my personal information secure?',
      answer: 'Yes, we use 256-bit SSL encryption and follow industry-standard security practices. All personal data is encrypted at rest and in transit. We comply with data protection regulations and undergo regular security audits.'
    },
    {
      id: 5,
      category: 'funding',
      question: 'How long do deposits take to process?',
      answer: 'Internal transfers between your wallets are instant. External deposits typically take 5-10 minutes for stable coins. Processing times may vary depending on network congestion and your chosen payment method.'
    },
    {
      id: 6,
      category: 'account',
      question: 'What is KYC verification and why is it required?',
      answer: 'KYC (Know Your Customer) is a regulatory requirement to verify your identity. This helps prevent money laundering and ensures compliance with financial regulations. The process involves submitting government-issued ID and proof of address.'
    },
    {
      id: 7,
      category: 'funding',
      question: 'What stable coins do you support?',
      answer: 'We currently support USDT (Tether) and USDC (USD Coin) on the Polygon network. Both are fully backed stable coins pegged to the US Dollar, providing stability and reliability for your transactions.'
    },
    {
      id: 8,
      category: 'technical',
      question: 'What should I do if my transaction is stuck?',
      answer: 'If your transaction appears stuck, first check the blockchain explorer using your transaction hash. Network congestion can cause delays. Contact our support team if a transaction has been pending for more than 30 minutes.'
    },
    {
      id: 9,
      category: 'account',
      question: 'Can I have both personal and vendor wallets?',
      answer: 'Yes, you can have both wallet types. Personal wallets are for individual use, while vendor wallets are designed for trading platforms and financial intermediaries. Each has different features and verification requirements.'
    },
    {
      id: 10,
      category: 'security',
      question: 'How do you prevent fraud and money laundering?',
      answer: 'We use advanced fraud detection systems, automated transaction monitoring, and comply with AML/KYC regulations. All transactions are screened for suspicious activity, and we report to relevant authorities when required.'
    },
    {
      id: 11,
      category: 'funding',
      question: 'Are there any fees for using TapTapUp?',
      answer: 'Fees vary by payment method and transaction type. Internal transfers are free. External transfers have a $2.50 network fee. Payment processing fees range from 2.5% to 3.2% depending on your chosen method.'
    },
    {
      id: 12,
      category: 'technical',
      question: 'Can I cancel a transaction after submitting it?',
      answer: 'Once a transaction is confirmed on the blockchain, it cannot be canceled or reversed. However, if a transaction is still pending in our system, you may be able to cancel it. Contact support immediately for assistance.'
    },
    {
      id: 13,
      category: 'account',
      question: 'What documents do I need for verification?',
      answer: 'For basic verification, you need a government-issued photo ID (passport, driver\'s license, or national ID) and a proof of address document (utility bill, bank statement) dated within the last 3 months.'
    },
    {
      id: 14,
      category: 'security',
      question: 'How do I enable two-factor authentication?',
      answer: 'Two-factor authentication can be enabled in your account settings. We recommend using an authenticator app like Google Authenticator or Authy for the best security. SMS-based 2FA is also available as an alternative.'
    },
    {
      id: 15,
      category: 'technical',
      question: 'Is there a mobile app available?',
      answer: 'Currently, TapTapUp is available as a responsive web application optimized for mobile devices. A dedicated mobile app is in development and will be released soon with additional features.'
    },
    {
      id: 16,
      category: 'funding',
      question: 'Can I withdraw funds back to my payment method?',
      answer: 'Currently, TapTapUp focuses on wallet funding services. Withdrawals are typically processed through blockchain transactions to external wallet addresses. Check with your payment provider for their policies on crypto-related transactions.'
    },
    {
      id: 17,
      category: 'account',
      question: 'How do I close my account?',
      answer: 'To close your account, contact our support team. We\'ll guide you through the process, which includes withdrawing any remaining balances and completing any pending transactions. Some data may be retained for regulatory compliance.'
    },
    {
      id: 18,
      category: 'security',
      question: 'What happens if I lose access to my account?',
      answer: 'If you lose access to your account, use the "Forgot Password" feature or contact support. You\'ll need to verify your identity through the same documents used during registration. Our team will help you regain secure access.'
    },
    {
      id: 19,
      category: 'technical',
      question: 'Do you support multiple cryptocurrencies?',
      answer: 'Currently, we focus on stable coins (USDT and USDC) to provide price stability. We may add support for other cryptocurrencies in the future based on user demand and regulatory clarity.'
    },
    {
      id: 20,
      category: 'technical',
      question: 'What blockchain networks do you use?',
      answer: 'We primarily use the Ethereum network (ERC-20) for stable coin transactions. We\'re evaluating other networks like Polygon and BSC for lower fees and faster transactions. Network selection depends on your needs and preferences.'
    }
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (id: number) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Frequently Asked Questions</h1>
        <p className="text-gray-400">Find answers to common questions about TapTapUp</p>
      </div>

      {/* Search Bar */}
      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-[#0F1419] border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:border-[#61CE70] focus:ring-1 focus:ring-[#61CE70] transition-colors"
            placeholder="Search for questions..."
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Categories */}
        <div className="lg:col-span-1">
          <div className="bg-[#0F1419] border border-gray-700 rounded-2xl p-6 sticky top-8">
            <h3 className="text-lg font-bold text-white mb-4">Categories</h3>
            <div className="space-y-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center justify-between w-full p-3 rounded-lg transition-colors text-left ${
                    activeCategory === category.id
                      ? 'bg-[#61CE70] text-white'
                      : 'text-gray-400 hover:text-white hover:bg-[#1A1F2B]'
                  }`}
                >
                  <span className="text-sm">{category.name}</span>
                  <span className="text-xs bg-gray-600 px-2 py-1 rounded-full">
                    {category.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Contact Support */}
            <div className="mt-8 pt-6 border-t border-gray-700">
              <h4 className="text-white font-medium mb-3">Need More Help?</h4>
              <div className="space-y-2">
                <button className="flex items-center space-x-2 w-full p-2 text-gray-400 hover:text-white hover:bg-[#1A1F2B] rounded-lg transition-colors">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-sm">Live Chat</span>
                </button>
                <button className="flex items-center space-x-2 w-full p-2 text-gray-400 hover:text-white hover:bg-[#1A1F2B] rounded-lg transition-colors">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">Email Support</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ List */}
        <div className="lg:col-span-3">
          <div className="space-y-4">
            {filteredFAQs.length === 0 ? (
              <div className="bg-[#0F1419] border border-gray-700 rounded-2xl p-8 text-center">
                <p className="text-gray-400 mb-4">No questions found matching your search.</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setActiveCategory('all');
                  }}
                  className="text-[#61CE70] hover:text-[#50B060] transition-colors"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              filteredFAQs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-[#0F1419] border border-gray-700 rounded-2xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-[#1A1F2B] transition-colors"
                  >
                    <h3 className="text-white font-medium pr-4">{faq.question}</h3>
                    {openFAQ === faq.id ? (
                      <ChevronUp className="w-5 h-5 text-[#61CE70] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFAQ === faq.id && (
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-700 pt-4">
                        <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}